/**
 * Account and club settings — FR-AUTH-3, FR-AUTH-6, FR-ADMIN-1.
 *
 * Everything that is not the entry path and not a statistic lives here, which
 * is deliberate: the session screen is the product, and a settings control on
 * it is a control somebody can hit by accident while holding a racket.
 *
 * The password flow is the reason this screen exists at all. The specified
 * route is an emailed reset link (§4.3), and it is the right one — an admin
 * never sees a credential for an account somebody already controls. It also
 * does not work here: the club has no mail server, so the link is written to
 * the server's journal, where no member can reach it. So there are two paths,
 * and the difference between them matters:
 *
 *   • A *new* login has no owner to protect yet. Somebody has to say the first
 *     password out loud. It is generated, shown once, and expected to be
 *     replaced.
 *   • An *existing* account's password is changed by the person who holds it,
 *     with the current one in hand. That is this screen's main form.
 */

import { useState } from "react";
import { api, RequestFailed, type Me } from "../api.js";
import { BackupLink } from "../Frame.js";
import { InstallPrompt } from "../InstallPrompt.js";

export function Settings({
  me,
  onSignedOut,
  onShowGuide,
}: {
  me: Me;
  onSignedOut: () => void;
  onShowGuide?: () => void;
}): React.JSX.Element {
  return (
    <>
      <h2>Your account</h2>
      <div className="block">
        <h3>Signed in</h3>
        <p>
          {me.displayName} · {me.role}
        </p>
      </div>

      <ChangePassword onSignedOut={onSignedOut} />

      {/* The roster and the guest list used to hang off the bottom of this
          screen for admins. They are the club's, not your account's, and they
          now live behind the Admin section of the personal page (FR-STAT-18). */}

      <h2>Elsewhere</h2>
      <div className="stack">
        {/*
          AC-15 — also here, not only on the start screen.

          The start screen is skipped whenever there is exactly one session
          today, which is the normal case, so on a session morning the install
          prompt was never rendered at all. The one day the club would most
          want the app on the home screen is the one day it never offered.
        */}
        <InstallPrompt />
        {/* FR-HELP-1 — beside the backup tracker, because both are things you
            want to have found *before* the morning you need them. */}
        {onShowGuide ? (
          <button className="quiet" onClick={onShowGuide}>
            How this works — getting started
          </button>
        ) : null}
        <BackupLink />
        <p className="muted small">
          The backup tracker is the same paper-equivalent the club used before
          this app. It needs no account and no signal, and it is worth knowing
          how to reach it before the morning you need it.
        </p>
        <button
          className="quiet"
          onClick={() => {
            void api.logout().finally(onSignedOut);
          }}
        >
          Sign out
        </button>
      </div>
    </>
  );
}

/**
 * FR-AUTH-3. Every device is signed out on success — including this one, which
 * is why the screen says so before the button is pressed rather than after.
 */
function ChangePassword({ onSignedOut }: { onSignedOut: () => void }): React.JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirm !== "" && confirm !== next;

  return (
    <div className="block">
      <h3>Change your password</h3>
      <div className="stack">
        <label>
          Current password
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
        <label>
          New password
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </label>
        <label>
          New password again
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>

        <p className="muted small">
          At least 10 characters. Changing it signs you out everywhere, this
          phone included — you will sign in again with the new one.
        </p>

        {mismatch ? <p className="error">Those two do not match.</p> : null}
        {error ? <p className="error">{error}</p> : null}

        <button
          className="primary"
          disabled={busy || !current || !next || mismatch || confirm === ""}
          onClick={() => {
            setBusy(true);
            setError(null);
            api
              .changePassword(current, next)
              .then(onSignedOut)
              .catch((e: unknown) =>
                setError(
                  e instanceof RequestFailed
                    ? e.message
                    : "Could not change your password. Are you online?",
                ),
              )
              .finally(() => setBusy(false));
          }}
        >
          Change password
        </button>
      </div>
    </div>
  );
}
